import api from './client'

export type SharingMode = 'private' | 'tenant' | 'users' | 'roles' | 'public_link'
export type SharePrincipalType = 'user' | 'role' | 'tenant'
export type SharePermission = 'view' | 'edit'

export interface DocumentSummary {
  id: string
  tenant_id: string
  project: string
  page_obj_name: string
  owner_id: string
  title: string
  sharing_mode: SharingMode
  created_at: string | null
  updated_at: string | null
  can_edit?: boolean
}

export interface DocumentBlock {
  id: string
  document_id: string
  parent_id: string | null
  position: number
  type: string
  properties: Record<string, unknown>
  created_by: string
  last_edited_by: string
  created_at: string | null
  updated_at: string | null
}

export interface DocumentPermissions {
  can_view: boolean
  can_edit: boolean
  can_share: boolean
  can_delete: boolean
}

export interface DocumentDetail {
  document: DocumentSummary
  blocks: DocumentBlock[]
  permissions: DocumentPermissions
  via?: 'public_link'
}

export interface DocumentShare {
  id: number
  document_id: string
  principal_type: SharePrincipalType
  principal_id: string
  permission: SharePermission
  granted_by: string
  granted_at: string | null
}

export async function createDocument(input: {
  project: string
  page_obj_name: string
  title?: string
  sharing_mode?: SharingMode
}): Promise<DocumentSummary> {
  const { data } = await api.post('/documents', input)
  return data.document
}

export async function listDocuments(filters?: {
  project?: string
  page_obj_name?: string
}): Promise<DocumentSummary[]> {
  const { data } = await api.get('/documents', { params: filters })
  return data.documents
}

export async function getDocument(id: string): Promise<DocumentDetail> {
  const { data } = await api.get(`/documents/${id}`)
  return {
    document: data.document,
    blocks: data.blocks,
    permissions: data.permissions,
  }
}

export async function getPublicDocument(token: string): Promise<DocumentDetail> {
  const { data } = await api.get(`/documents/public/${token}`)
  return {
    document: data.document,
    blocks: data.blocks,
    permissions: data.permissions,
    via: data.via,
  }
}

export async function updateDocument(id: string, patch: { title?: string; sharing_mode?: SharingMode }): Promise<DocumentSummary> {
  const { data } = await api.patch(`/documents/${id}`, patch)
  return data.document
}

export async function deleteDocument(id: string): Promise<void> {
  await api.delete(`/documents/${id}`)
}

export async function updateBlock(docId: string, blockId: string, properties: Record<string, unknown>): Promise<DocumentBlock> {
  const { data } = await api.patch(`/documents/${docId}/blocks/${blockId}`, { properties })
  return data.block
}

export async function insertBlock(docId: string, block: {
  type: string
  properties?: Record<string, unknown>
  parent_id?: string | null
  after_block_id?: string | null
  id?: string
}): Promise<DocumentBlock> {
  const { data } = await api.post(`/documents/${docId}/blocks`, block)
  return data.block
}

export async function deleteBlock(docId: string, blockId: string): Promise<void> {
  await api.delete(`/documents/${docId}/blocks/${blockId}`)
}

export async function listShares(docId: string): Promise<{ shares: DocumentShare[]; sharing_mode: SharingMode }> {
  const { data } = await api.get(`/documents/${docId}/shares`)
  return { shares: data.shares, sharing_mode: data.sharing_mode }
}

export async function addShare(docId: string, share: {
  principal_type: SharePrincipalType
  principal_id: string
  permission: SharePermission
}): Promise<DocumentShare> {
  const { data } = await api.post(`/documents/${docId}/shares`, share)
  return data.share
}

export async function revokeShare(docId: string, shareId: number): Promise<void> {
  await api.delete(`/documents/${docId}/shares/${shareId}`)
}

export async function createPublicLink(docId: string, permission: SharePermission = 'view'): Promise<{ token: string; permission: SharePermission; id: number }> {
  const { data } = await api.post(`/documents/${docId}/public-link`, { permission })
  return { token: data.token, permission: data.permission, id: data.id }
}

export async function revokePublicLink(docId: string): Promise<void> {
  await api.delete(`/documents/${docId}/public-link`)
}
